import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

interface TacticalTimerState {
  isInactive: boolean;
  inactiveMinutes: number;
  lastPingTime: Date | null;
  connectionStatus: 'connected' | 'disconnected' | 'error';
}

const PING_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const INACTIVITY_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes

export function useTacticalTimer(employeeName: string, enabled: boolean = true) {
  const [state, setState] = useState<TacticalTimerState>({
    isInactive: false,
    inactiveMinutes: 0,
    lastPingTime: null,
    connectionStatus: 'disconnected',
  });

  const lastActivityRef = useRef<Date>(new Date());
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityCheckRef = useRef<NodeJS.Timeout | null>(null);

  const updateLastActivity = () => {
    lastActivityRef.current = new Date();
    setState(prev => ({ ...prev, isInactive: false, inactiveMinutes: 0 }));
  };

  const pingBackend = async () => {
    if (!enabled) return;

    try {
      await api.employee.ping?.() || fetch('/api/employee/ping', {
        credentials: 'include',
      });
      setState(prev => ({
        ...prev,
        lastPingTime: new Date(),
        connectionStatus: 'connected',
      }));
    } catch (error) {
      console.error('Tactical timer ping failed:', error);
      setState(prev => ({ ...prev, connectionStatus: 'error' }));
    }
  };

  const checkInactivity = () => {
    const now = new Date();
    const timeSinceActivity = now.getTime() - lastActivityRef.current.getTime();
    const inactiveMinutes = Math.floor(timeSinceActivity / 60000);

    if (timeSinceActivity > INACTIVITY_THRESHOLD_MS) {
      setState(prev => ({
        ...prev,
        isInactive: true,
        inactiveMinutes,
      }));
    } else {
      setState(prev => ({
        ...prev,
        isInactive: false,
        inactiveMinutes: 0,
      }));
    }
  };

  useEffect(() => {
    if (!enabled) {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (activityCheckRef.current) clearInterval(activityCheckRef.current);
      return;
    }

    // Initial ping
    pingBackend();

    // Set up 10-minute ping interval
    pingIntervalRef.current = setInterval(pingBackend, PING_INTERVAL_MS);

    // Set up inactivity check (every minute)
    activityCheckRef.current = setInterval(checkInactivity, 60000);

    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateLastActivity);
    });

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (activityCheckRef.current) clearInterval(activityCheckRef.current);
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
    };
  }, [enabled, employeeName]);

  return {
    ...state,
    updateLastActivity,
  };
}
